import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import OfferAdditionalDataCardContainer from '../components/OfferAdditionalDataCardContainer';
import useEventCallback from 'utils/hooks/useEventCallback';
import { shape, lessThan, when, between, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { isValidDateFormat, lessOrEqualThan } from 'utils/pure-validation/primitiveValidators';
import moment from 'moment';
import { useValidation } from '@totalsoft/pure-validations-react';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { ADDITIONAL_DATA_QUERY, REFERENCE_INTERESTS_QUERY, OFFER_HEADER_QUERY } from '../queries';
import documentTypeCodes from 'constants/documentTypeIds';
import { OFFER_ADDITIONAL_DATA_UPDATED, VARIABLE_INTEREST_COMPUTED } from '../subscriptions';
import { useMutation } from '@apollo/client';
import { ADDITIONAL_DATA_INPUT_MUTATION, VARIABLE_INTEREST_MUTATION } from '../mutations';
import { useToast } from 'utils/hooks';
import { useTranslation } from 'react-i18next';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useError } from 'customHooks/errorHandling';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useDictionary } from '../hooks/useDictionary';
import { get, useChangeTrackingLens, set } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { useSelector, shallowEqual } from 'react-redux';

const OfferAdditionalDataContainer = ({ documentId, siteId, currencyCode }) => {
  const showError = useError();
  const addToast = useToast();
  const { t } = useTranslation();

  const [variableInterestComputing, setVariableInterestComputing] = useState(false);
  const [savingAdditionalData, setSavingAdditionalData] = useState(false);
  const [isFacilityFrame, setIsFacilityFrame] = useState(false);
  const [documentTypeId, setDocumentTypeId] = useState();
  const [cardExpanded, setExpanded] = useState(false);
  const [additionalDataLoaded, setAdditionalDataLoaded] = useState(false);

  const userIsDealer = useSelector(state => !state.userInfo.dealerIsSelfPartner && !state.userInfo.userIsAdmin, shallowEqual);
  const { supplierConversionFactor } = useSelector(state => state.params.items, shallowEqual);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({
    additionalData: {},
    isDirty: false,
    filteredOfferReferenceInterests: [],
    facilityState: isFacilityFrame ? { facilityDataIsDirty: false, facilityDataShouldSave: false, facilityDataIsSaving: false } : undefined
  });

  const onExpand = useCallback((_, expanded) => {
    setExpanded(!expanded);
  }, []);

  const { loading: additionalDataLoading } = useQueryWithErrorHandling(ADDITIONAL_DATA_QUERY, {
    variables: { documentId, siteId },
    skip: !cardExpanded,
    onCompleted: data => {
      resetLens({
        ...(lens |> get),
        additionalData: data?.additionalData
      });
      setAdditionalDataLoaded(true);
    }
  });

  const { loading: offerHeaderLoading } = useQueryWithErrorHandling(OFFER_HEADER_QUERY, {
    variables: { documentId, siteId },
    skip: !cardExpanded,
    onCompleted: data => {
      setIsFacilityFrame(data.offerHeaders.documentTypeId === documentTypeCodes.FacilityFrameOffer);
      setDocumentTypeId(data.offerHeaders.documentTypeId);
    }
  });

  const referenceInterests = useDictionary(REFERENCE_INTERESTS_QUERY, {
    skip: !cardExpanded
  });

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setVariableInterestComputing(false);
    }
  });

  const { storeKey: saveStoreKey } = useServerErrorHandling({
    effect: _ => {
      setSavingAdditionalData(false);
      let additionalDataInformation = lens |> get;
      additionalDataInformation = additionalDataInformation.facilityState
        ? {
            ...additionalDataInformation,
            isDirty: true,
            facilityState: {
              ...additionalDataInformation.facilityState,
              facilityDataShouldSave: additionalDataInformation?.facilityState?.facilityDataIsDirty
            }
          }
        : {
            ...additionalDataInformation,
            isDirty: true
          };
      (lens |> set)(additionalDataInformation);
    }
  });

  const [computeVariableInterest] = useMutation(VARIABLE_INTEREST_MUTATION, {
    skip: !cardExpanded,
    onError: error => {
      showError(error.message);
      setVariableInterestComputing(false);
    },
    onCompleted: r => {
      const { correlationId } = r.computeVariableInterest;
      storeKey({ correlationId });
    }
  });

  const [saveAdditionalData] = useMutation(ADDITIONAL_DATA_INPUT_MUTATION, {
    skip: !cardExpanded,
    onError: error => {
      showError(error.message);
      savingAdditionalData(false);
    },
    onCompleted: r => {
      const { correlationId } = r.updateOffersAdditionalData;
      saveStoreKey({ correlationId });
    }
  });

  useEffect(() => {
    let additionalDataInformation = lens |> get;
    additionalDataInformation = {
      ...additionalDataInformation,
      facilityState: isFacilityFrame ? { facilityDataIsDirty: false, facilityDataShouldSave: false, facilityDataIsSaving: false } : null
    };
    resetLens({ ...additionalDataInformation });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacilityFrame]);

  useEffect(() => {
    if (referenceInterests.loading || !additionalDataLoaded || referenceInterests.data.length < 1) return;
    const filteredOfferReferenceInterests = referenceInterests.data.filter(
      m => (lens.additionalData.isFixed |> get) === m.isFixed && m.currencyCode === currencyCode
    );
    resetLens({ ...(lens |> get), filteredOfferReferenceInterests: filteredOfferReferenceInterests });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalDataLoaded, currencyCode, referenceInterests.data]);

  useEffect(() => {
    if (!isDirty(dirtyInfo)) return;
    if (additionalDataLoading || referenceInterests.loading) return;
    if (!(lens.additionalData |> get) || (lens.additionalData?.isFixed |> get)) return;
    if (variableInterestComputing) {
      computeVariableInterest({
        variables: {
          input: { documentId, siteId, interestTypeId: lens.additionalData?.interestTypeId |> get }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableInterestComputing]);

  useGlobalSubscription(VARIABLE_INTEREST_COMPUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const newInterestVariablePercent = data.variableInterestComputed.variableInterest;
      let additionalDataInformation = lens |> get;
      additionalDataInformation = {
        ...additionalDataInformation,
        additionalData: {
          ...additionalDataInformation.additionalData,
          interestVariablePercent: data.variableInterestComputed.variableInterest,
          variableInterestDate: data.variableInterestComputed.variableInterestDate
        },
        isDirty: isDirty(dirtyInfo) || additionalDataInformation.additionalData.interestVariablePercent !== newInterestVariablePercent
      };
      setVariableInterestComputing(false);
      (lens |> set)(additionalDataInformation);
    }
  });

  useGlobalSubscription(OFFER_ADDITIONAL_DATA_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: _ => {
      setSavingAdditionalData(false);
      addToast(t('OfferEdit.AdditionalData.AdditionalDataMessage.AdditionalDataUpdated'), 'success');
    }
  });

  const validatorAdditionalData = useCallback(() => {
    return shape({
      variableInterestDate: [lessThan(moment().add(1, 'days').utc().format('YYYY-MM-DD')), isValidDateFormat] |> stopOnFirstFailure,
      clientConversionFactor: between(0, 100),
      supplierConversionFactor: [lessOrEqualThan(supplierConversionFactor) |> when(userIsDealer), between(0, 100)] |> stopOnFirstFailure
    });
  }, [supplierConversionFactor, userIsDealer]);

  const memoizedValidatorAdditionalData = useMemo(() => validatorAdditionalData(), [validatorAdditionalData]);

  const [validationAdditionalData, validateAdditionalData] = useValidation(memoizedValidatorAdditionalData, dirtyInfo);

  const handlePropertyChange = useEventCallback(
    (prop, value, prop2, value2) => {
      const isFixed = prop === 'isFixed' && value;
      let updatedAdditionalData = {
        ...(lens.additionalData |> get),
        [prop]: value,
        ...(isFixed && { variableInterestDate: null, interestVariablePercent: null, interestTypeId: null })
      };
      if (prop2) updatedAdditionalData[prop2] = value2;
      if (prop === 'isFixed') {
        const filteredOfferReferenceInterests = referenceInterests.data.filter(
          m => updatedAdditionalData.isFixed === m.isFixed && m.currencyCode === currencyCode
        );
        (lens.filteredOfferReferenceInterests |> set)(filteredOfferReferenceInterests);
      }
      if (prop === 'interestTypeId') {
        setVariableInterestComputing(true);
      }
      (lens.additionalData |> set)(updatedAdditionalData);
    },
    [lens.additionalData]
  );

  const handleFacilityStateChange = useEventCallback(
    eventType => {
      let updatedFacilityState;
      switch (eventType) {
        case 'OnPropertyChange':
          updatedFacilityState = {
            facilityDataIsDirty: true,
            facilityDataShouldSave: false,
            facilityDataIsSaving: false
          };
          break;
        case 'OnSaveStarted':
          updatedFacilityState = {
            facilityDataIsDirty: true,
            facilityDataShouldSave: false,
            facilityDataIsSaving: true
          };
          break;
        case 'OnSaveSucceeded':
          updatedFacilityState = {
            facilityDataIsDirty: false,
            facilityDataShouldSave: false,
            facilityDataIsSaving: false
          };
          break;
        case 'OnSaveFailed':
          updatedFacilityState = {
            facilityDataIsDirty: true,
            facilityDataShouldSave: false,
            facilityDataIsSaving: false
          };
          break;
        default:
          return;
      }
      (lens.facilityState |> set)(updatedFacilityState);
    },
    [lens.facilityState]
  );

  const handleSave = useEventCallback(() => {
    const additionalData = lens.additionalData |> get;
    if (!validateAdditionalData(additionalData)) {
      return;
    }

    let additionalDataInformation = lens |> get;
    saveAdditionalData({
      variables: {
        input: additionalData
      }
    });
    additionalDataInformation = additionalDataInformation.facilityState
      ? {
          ...additionalDataInformation,
          isDirty: false,
          facilityState: {
            ...additionalDataInformation.facilityState,
            facilityDataShouldSave: additionalDataInformation?.facilityState?.facilityDataIsDirty
          }
        }
      : {
          ...additionalDataInformation,
          isDirty: false
        };
    resetLens({ ...additionalDataInformation });
    setSavingAdditionalData(true);
  }, []);

  const handleInterestVariablePercentRefresh = useCallback(() => {
    computeVariableInterest({
      variables: {
        input: { documentId, siteId, interestTypeId: lens.additionalData.interestTypeId |> get }
      }
    });
    setVariableInterestComputing(true);
  }, [computeVariableInterest, documentId, lens.additionalData.interestTypeId, siteId]);

  return (
    <OfferAdditionalDataCardContainer
      onPropertyChange={handlePropertyChange}
      onSave={handleSave}
      onExpand={onExpand}
      cardExpanded={cardExpanded}
      validation={validationAdditionalData}
      saving={savingAdditionalData || (isFacilityFrame && (lens.facilityState?.facilityDataIsSaving |> get))}
      isDirty={isDirty(dirtyInfo) || (isFacilityFrame && (lens.facilityState?.facilityDataIsDirty |> get))}
      documentId={documentId}
      documentTypeId={documentTypeId}
      siteId={siteId}
      handleFacilityStateChange={handleFacilityStateChange}
      isFacilityFrame={isFacilityFrame}
      onInterestVariablePercentRefresh={handleInterestVariablePercentRefresh}
      variableInterestComputing={variableInterestComputing}
      lens={lens}
      additionalDataLoaded={additionalDataLoaded}
      offerHeaderLoading={offerHeaderLoading}
    />
  );
};

OfferAdditionalDataContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  referenceInterests: PropTypes.array,
  isLoaded: PropTypes.bool,
  currencyCode: PropTypes.string.isRequired,
  isFacilityFrame: PropTypes.bool
};

export default OfferAdditionalDataContainer;
